import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { BiArrowBack } from 'react-icons/bi';
import { platformAPI } from '../../../services/platformAPI';
import WorkspaceDataTable from '../../../components/workspace/WorkspaceDataTable';
import WorkspacePageHeader from '../../../components/workspace/WorkspacePageHeader';
import { useAdminRecordDelete } from '../../../components/workspace/useAdminRecordDelete';
import { revisionColumn, useMasterRevision } from '../../../components/workspace/useMasterRevision';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { canManageMasters } from '../../../utils/permissions';
import { dropdownRoot, settingsBase } from './settingsPaths';
import '../tender/CustomerMaster.scss';

const DropdownValuesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { catalog } = useParams();
  const user = useSelector((state) => state.auth.user);
  const base = settingsBase(location.pathname);
  const root = dropdownRoot(location.pathname);
  const inMasters = location.pathname.includes('/masters/lists');
  const [meta, setMeta] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const response = await platformAPI.listDropdownValues(catalog);
      setMeta(response.data?.data?.catalog || null);
      setRows(response.data?.data?.values || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load this list.');
    } finally {
      setLoading(false);
    }
  }, [catalog]);

  useEffect(() => {
    load();
  }, [load]);

  useDocumentTitle(meta?.title || 'Dropdown list');

  const columns = useMemo(() => [
    {
      key: 'label',
      label: 'Label',
      mobileTitle: true,
      render: (value) => <span className="cust-name__title">{value}</span>
    },
    { key: 'code', label: 'Code', width: '180px' },
    { key: 'sortOrder', label: 'Order', width: '90px' },
    revisionColumn,
    {
      key: 'status',
      label: 'Status',
      width: '120px',
      render: (value) => (
        <span className={`cust-status cust-status--${value === 'active' ? 'active' : 'inactive'}`}>
          {value === 'active' ? 'Active' : 'Inactive'}
        </span>
      )
    }
  ], []);

  const admin = useAdminRecordDelete({
    entity: 'dropdown',
    catalog,
    noun: 'value',
    plural: 'values',
    reload: load,
    label: (row) => row.label
  });
  const revision = useMasterRevision({ entity: 'dropdown', catalog });

  if (!canManageMasters(user?.permissions, user?.roles)) {
    return <Alert variant="warning">A tender administrator maintains dropdown lists.</Alert>;
  }

  if (loading) return <Spinner animation="border" size="sm" />;

  return (
    <div className="cust-page">
      <button type="button" className="cust-page__back" onClick={() => navigate(inMasters ? '/apps/tender/masters' : `${base}/dropdowns`)}>
        <BiArrowBack />
        {inMasters ? 'Masters' : 'Dropdown master'}
      </button>
      <WorkspacePageHeader
        title={meta?.title || 'Dropdown list'}
        help="settings"
        subtitle={meta?.description || 'Values in this list.'}
        actions={(
          <>
            <span className="cust-page__count">Total records {rows.length}</span>
            <Button size="sm" variant="primary" onClick={() => navigate(`${root}/${catalog}/new`)}>
              New
            </Button>
          </>
        )}
      />
      {meta?.sample ? <Alert variant="info">These starting values are editable. They are not a confirmed firm policy.</Alert> : null}
      {error ? <Alert variant="danger">{error}</Alert> : null}
      {admin.notice}
      <WorkspaceDataTable
        downloadPermission="masters.download"
        title={meta?.title || 'Values'}
        data={rows}
        columns={columns}
        searchPlaceholder="Search label or code…"
        pageSize={12}
        onView={(row) => navigate(`${root}/${catalog}/${row._id}`)}
        onEdit={(row) => navigate(`${root}/${catalog}/${row._id}/edit`)}
        {...admin.tableProps}
        rowActions={revision.wrapActions(admin.wrapActions(() => [
          { type: 'view', label: 'View' },
          { type: 'edit', label: 'Edit' },
          { type: 'delete', label: 'Delete', variant: 'danger' }
        ]))}
      />
      {admin.modal}
      {revision.modals}
    </div>
  );
};

export default DropdownValuesPage;
